import dayjs from "dayjs";
import { isAxiosError } from "axios";
import { useState } from "react";
import {
  type ActionFunction,
  type LoaderFunction,
  Navigate,
  redirect,
  useActionData,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSubmit,
} from "react-router-dom";

import {
  getGetMyMembershipsQueryOptions,
  requestMembershipFreeze,
  useGetMyMemberships,
} from "@/api/orval/platform-user-membership/platform-user-membership";
import {
  hasCurrentMembershipBillingPeriod,
  invalidateMembershipFreezeQueries,
} from "./membershipHelper";
import cx from "classnames";

import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import Minus from "atoms/icon/icons/ic_minus_small_ol.svg";
import Plus from "atoms/icon/icons/ic_plus_small_ol.svg";
import { TextButton } from "atoms/text-button/TextButton";
import { TextArea } from "atoms/TextArea/TextArea";
import { useTranslation } from "@/core/i18n";
import { usePlatform } from "hooks/usePlatform";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { getBillingIntervalBase, parseBillingPeriodName } from "utils/billingPeriod.helpers";
import { readFormData } from "toolbox/ReadFormData";

// Beyond this many lengths the pills wrap into a wall; a stepper stays one row at any maximum.
const MAX_FREEZE_LENGTH_CHIPS = 6;

const load = async (platformUserMembershipId: string) => {
  const userMemberships = await queryClient.fetchQuery(getGetMyMembershipsQueryOptions());

  const userMembership = userMemberships.find(
    (m) => m.platformUserMembershipId === platformUserMembershipId,
  );

  if (
    !userMembership ||
    !userMembership.membership.allowFreeze ||
    userMembership.membership.maxFreezeBillingPeriods < 1 ||
    userMembership.activeMembershipFreezeId
  ) {
    return redirect("/wallet/memberships/manage");
  }

  return {
    platformUserMembershipId,
    userMemberships,
  };
};

type LoaderData = Exclude<Awaited<ReturnType<typeof load>>, Response>;
export const walletMembershipFreezeLoader: LoaderFunction = ({
  params: { platformUserMembershipId },
}) => {
  if (!platformUserMembershipId) {
    return redirect("..");
  }

  return load(platformUserMembershipId);
};

export const walletMembershipFreezeAction: ActionFunction = async ({ request }) => {
  const { platformUserMembershipId, billingPeriods, requestReason } = await readFormData<{
    platformUserMembershipId: string;
    billingPeriods: string;
    requestReason: string;
  }>(request);

  const trimmedReason = requestReason?.trim();

  try {
    await requestMembershipFreeze(platformUserMembershipId, {
      billingPeriods: Number(billingPeriods),
      requestReason: trimmedReason ? trimmedReason : undefined,
    });
  } catch (error) {
    if (
      isAxiosError<{ title: string }>(error) &&
      error.response?.status === 400 &&
      error.response.data.title === "error.membershipFreezeBillingPeriodEnded"
    ) {
      return { billingPeriodEnded: true };
    }
    throw error;
  }

  await invalidateMembershipFreezeQueries(queryClient);

  return redirect("/wallet/memberships/manage");
};

export const WalletMembershipFreezePage = () => {
  const { platformUserMembershipId, userMemberships: initialUserMemberships } =
    useLoaderData() as LoaderData;

  const actionData = useActionData() as { billingPeriodEnded: true } | undefined;
  const navigate = useNavigate();
  const submit = useSubmit();
  const navigation = useNavigation();
  const isFlexibleMembershipsEnabled = useFeatureFlag("flexible_membership_terms");
  const { platform } = usePlatform();
  const { t } = useTranslation();

  const { data: userMemberships } = useGetMyMemberships({
    query: { initialData: initialUserMemberships },
  });

  const [selectedBillingPeriods, setSelectedBillingPeriods] = useState("1");
  const [requestReason, setRequestReason] = useState("");

  const userMembership = userMemberships.find(
    (m) => m.platformUserMembershipId === platformUserMembershipId,
  );

  if (!userMembership) {
    return <Navigate replace to="/wallet/memberships/manage" />;
  }

  const { name, billingPeriodInterval, billingPeriodIntervalCount, maxFreezeBillingPeriods } =
    userMembership.membership;

  const baseBillingPeriodInterval = parseBillingPeriodName(billingPeriodInterval);
  const billingPeriodIntervalName = isFlexibleMembershipsEnabled
    ? (getBillingIntervalBase(baseBillingPeriodInterval) as dayjs.ManipulateType)
    : "month";
  const billingPeriodLength = isFlexibleMembershipsEnabled ? billingPeriodIntervalCount : 1;

  const formatFreezeLength = (billingPeriods: number) => {
    const count = billingPeriods * billingPeriodLength;
    switch (billingPeriodIntervalName) {
      case "day":
        return t("walletMembershipFreezePage.lengthDay", { count });
      case "week":
        return t("walletMembershipFreezePage.lengthWeek", { count });
      case "year":
        return t("walletMembershipFreezePage.lengthYear", { count });
      default:
        return t("walletMembershipFreezePage.lengthMonth", { count });
    }
  };

  const freezeLengthOptions = Array.from(
    { length: maxFreezeBillingPeriods },
    (_, index) => `${index + 1}`,
  );

  const billingPeriodEnded =
    actionData?.billingPeriodEnded === true || !hasCurrentMembershipBillingPeriod(userMembership);
  const selectedPeriods = Number(selectedBillingPeriods);
  const startsAt = dayjs(userMembership.expiration);
  const resumesAt = startsAt.add(
    Number(selectedBillingPeriods) * billingPeriodLength,
    billingPeriodIntervalName,
  );

  return (
    <div className="relative flex h-full flex-col bg-white">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          {t("walletMembershipFreezePage.header")}
        </div>
      </TopNav>
      <div className="flex grow flex-col gap-y-6 bg-white px-6 pb-6 pt-3 text-left">
        <div className="flex flex-col gap-y-1">
          <h1 className="text-title">{t("walletMembershipFreezePage.title")}</h1>
          <div className="text-body2 text-secondary">{name}</div>
        </div>

        {billingPeriodEnded ? (
          <div role="alert" className="rounded-xl bg-one p-4 text-body2 text-primary">
            {t("walletMembershipFreezePage.billingPeriodEnded")}
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-y-2">
              <span className="text-bold2 text-primary">
                {t("walletMembershipFreezePage.lengthLabel")}
              </span>
              {maxFreezeBillingPeriods > MAX_FREEZE_LENGTH_CHIPS ? (
                <div className="flex items-center justify-between rounded-full border border-black/10 p-1">
                  <button
                    type="button"
                    aria-label={t("walletMembershipFreezePage.shorter")}
                    disabled={selectedPeriods <= 1}
                    onClick={() => setSelectedBillingPeriods(`${selectedPeriods - 1}`)}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-one disabled:opacity-30"
                  >
                    <Icon svg={Minus} color="brandColor" size="xsmall" />
                  </button>
                  <span className="text-bold1 text-primary" aria-live="polite">
                    {formatFreezeLength(selectedPeriods)}
                  </span>
                  <button
                    type="button"
                    aria-label={t("walletMembershipFreezePage.longer")}
                    disabled={selectedPeriods >= maxFreezeBillingPeriods}
                    onClick={() => setSelectedBillingPeriods(`${selectedPeriods + 1}`)}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-one disabled:opacity-30"
                  >
                    <Icon svg={Plus} color="brandColor" size="xsmall" />
                  </button>
                </div>
              ) : (
                <div
                  role="radiogroup"
                  aria-label={t("walletMembershipFreezePage.lengthLabel")}
                  className="flex flex-wrap gap-2"
                >
                  {freezeLengthOptions.map((option) => {
                    const isSelected = option === selectedBillingPeriods;
                    return (
                      <button
                        key={option}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        onClick={() => setSelectedBillingPeriods(option)}
                        className={cx(
                          "rounded-full border px-4 py-2 text-body2 transition-colors",
                          isSelected
                            ? "border-brand-color bg-brand-color text-white"
                            : "border-black/10 bg-white text-primary",
                        )}
                      >
                        {formatFreezeLength(Number(option))}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-y-3 rounded-xl bg-one p-4">
              <div className="flex items-center justify-between text-body2">
                <span className="text-secondary">
                  {t("walletMembershipFreezePage.startsLabel")}
                </span>
                <span className="text-bold2 text-primary">{startsAt.format("MMM D, YYYY")}</span>
              </div>
              <div className="flex items-center justify-between text-body2">
                <span className="text-secondary">
                  {t("walletMembershipFreezePage.resumesLabel")}
                </span>
                <span className="text-bold2 text-primary">{resumesAt.format("MMM D, YYYY")}</span>
              </div>
              <div className="border-t border-black/5 pt-3 text-body3 text-secondary">
                {t("walletMembershipFreezePage.noCharges")}{" "}
                {platform?.name
                  ? t("walletMembershipFreezePage.reviewNotice", { platformName: platform.name })
                  : t("walletMembershipFreezePage.reviewNoticeNoPlatform")}
              </div>
            </div>

            <TextArea
              label={t("walletMembershipFreezePage.requestReasonLabel")}
              placeholder={
                platform?.name
                  ? t("walletMembershipFreezePage.requestReasonPlaceholder", {
                      platformName: platform.name,
                    })
                  : t("walletMembershipFreezePage.requestReasonPlaceholderNoPlatform")
              }
              value={requestReason}
              onChange={setRequestReason}
              maxLength={500}
              rows={3}
              visible
            />
          </>
        )}

        <div className="grow" />
        <div className="flex flex-col items-center gap-y-2">
          {!billingPeriodEnded && (
            <Button
              fullWidth
              style="primary"
              disabled={navigation.state !== "idle"}
              onClick={() => {
                submit(
                  {
                    platformUserMembershipId,
                    billingPeriods: selectedBillingPeriods,
                    requestReason,
                  },
                  { method: "post" },
                );
              }}
            >
              {t("walletMembershipFreezePage.submit")}
            </Button>
          )}
          <TextButton variant="secondary" onClick={() => navigate("/wallet/memberships/manage")}>
            {t("walletMembershipFreezePage.neverMind")}
          </TextButton>
        </div>
      </div>
    </div>
  );
};
